import { EndpointService, type ID } from "@/types/api/api";
import { buildQueryKey } from "@/utils/query";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import type { IAPI } from "../APIService";

/**
 * Interface representing a single course returned by the courses list endpoint.
 * @interface ICourse
 * @property {ID} id — The course identifier.
 * @property {string} name — The course display name.
 * @property {number} order — The display order rank.
 * @property {string | null} description — Optional course description.
 * @property {number | null} theme_colour — Optional theme colour enum value.
 */
export interface ICourse {
  id: ID;
  name: string;
  order: number;
  description: string | null;
  theme_colour: number | null;
}

/**
 * Interface representing the courses service surface.
 * @interface ICoursesService
 * @property {function} list — Returns the list of courses for the active venue.
 * @property {boolean} list.enabled — When false, the underlying query is disabled.
 */
export interface ICoursesService {
  list: (options?: { enabled?: boolean }) => UseQueryResult<ICourse[], Error>;
}

export class CoursesService implements ICoursesService {
  api: IAPI;

  constructor(api: IAPI) {
    this.api = api;
  }

  list(options?: { enabled?: boolean }): UseQueryResult<ICourse[], Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["operations", "courses", "list"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      queryFn: async () => {
        const { data } = await this.api.get<AxiosResponse<ICourse[]>>(
          "/restaurant/courses/",
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
            params: {},
          },
        );
        return data;
      },
      enabled: options?.enabled ?? true,
    });
  }
}
